import { getChatCompletion } from './aiIntegrations/chatCompletion';
import { supabase } from '../lib/supabase';
import { generateAudioForTerms } from './glossaryAudioGenerationService';

/**
 * Scan imported LINE rows for archaic Shakespearean words and auto-populate glossary.
 * Generates definitions via OpenAI, saves them with ai_generated=true and
 * review_status='pending_review' so admins can approve or edit before publishing.
 * After definitions are saved, auto-generates pronunciation audio via OpenAI TTS.
 *
 * @param {Array} lines - Array of parsed line objects from csvProcessor (all types)
 * @param {string} playId - UUID of the newly created play
 * @param {string} playTitle - Title of the play for AI context
 * @param {string|null} userId - ID of the user performing the upload
 * @param {Function} [onProgress] - Optional callback(status: string)
 * @returns {Promise<{ added: number, skipped: number, terms: Array, audioSucceeded: number, audioFailed: number }>}
 */
export const scanAndPopulateGlossary = async (lines, playId, playTitle, userId, onProgress) => {
  try {
    // 1. Collect text from LINE type rows only
    const lineTexts = lines
      ?.filter(l => l?.lineType === 'LINE' && l?.dialogue?.trim())
      ?.map(l => l?.dialogue?.trim());

    if (!lineTexts || lineTexts?.length === 0) {
      return { added: 0, skipped: 0, terms: [], audioSucceeded: 0, audioFailed: 0 };
    }

    onProgress?.('Scanning lines for archaic words...');

    // 2. Batch lines into chunks to stay within token limits (~150 lines per batch)
    const BATCH_SIZE = 150;
    const batches = [];
    for (let i = 0; i < lineTexts?.length; i += BATCH_SIZE) {
      batches?.push(lineTexts?.slice(i, i + BATCH_SIZE));
    }

    // 3. Call OpenAI for each batch and collect all discovered words
    const allDiscoveredWords = new Map(); // word (lowercase) -> term object

    for (let batchIndex = 0; batchIndex < batches?.length; batchIndex++) {
      const batch = batches?.[batchIndex];
      onProgress?.(`Analyzing batch ${batchIndex + 1} of ${batches?.length}...`);

      const combinedText = batch?.join('\n');

      const messages = [
        {
          role: 'system',
          content:
            'You are a Shakespeare scholar and expert linguist specializing in Early Modern English. You identify archaic words that modern high school or college students would need help understanding.'
        },
        {
          role: 'user',
          content: `Analyze the following lines from the Shakespeare play "${playTitle}" and identify all archaic or difficult words that modern students would need help understanding.\n\nFor each word provide:\n1. The word itself (lowercase)\n2. A clear, concise definition (1-2 sentences) suitable for students\n3. Pronunciation guide in IPA format\n4. Difficulty level: easy, medium, or hard\n\nLines to analyze:\n${combinedText}\n\nReturn ONLY a valid JSON object with a "terms" array:\n{\n  "terms": [\n    {\n      "word": "thee",\n      "definition": "An archaic second-person singular pronoun meaning 'you' (as the object of a verb or preposition).",\n      "pronunciation": "/ðiː/",\n      "difficulty": "easy"\n    }\n  ]\n}\n\nRules:\n- Only include genuinely archaic or difficult words (not common modern words)\n- Avoid duplicates\n- Return ONLY the JSON object, no extra text`
        }
      ];

      try {
        const response = await getChatCompletion('OPEN_AI', 'gpt-4o-mini', messages, {
          max_completion_tokens: 3000,
          response_format: { type: 'json_object' }
        });

        // Extract content from Lambda response
        const content = response?.choices?.[0]?.message?.content;
        if (!content) continue;

        let parsed;
        try {
          parsed = JSON.parse(content);
        } catch {
          continue;
        }

        const terms = parsed?.terms || parsed?.words || (Array.isArray(parsed) ? parsed : []);

        terms?.forEach(term => {
          if (!term?.word || !term?.definition) return;
          const key = term?.word?.trim()?.toLowerCase();
          if (!allDiscoveredWords?.has(key)) {
            allDiscoveredWords?.set(key, {
              word: term?.word?.trim()?.toLowerCase(),
              definition: term?.definition?.trim(),
              pronunciation: term?.pronunciation?.trim() || '',
              difficulty: ['easy', 'medium', 'hard']?.includes(term?.difficulty)
                ? term?.difficulty
                : 'medium'
            });
          }
        });
      } catch (batchError) {
        console.error(`Glossary scan batch ${batchIndex + 1} error:`, batchError);
        // Continue with remaining batches
      }
    }

    if (allDiscoveredWords?.size === 0) {
      return { added: 0, skipped: 0, terms: [], audioSucceeded: 0, audioFailed: 0 };
    }

    onProgress?.('Checking existing glossary entries...');

    // 4. Fetch existing glossary terms (all, not just for this play) to deduplicate
    const { data: existingTerms, error: fetchError } = await supabase
      ?.from('glossary_terms')
      ?.select('term');

    if (fetchError) {
      console.error('Error fetching existing glossary terms:', fetchError);
      throw fetchError;
    }

    const existingSet = new Set(
      existingTerms?.map(t => t?.term?.trim()?.toLowerCase()) || []
    );

    // 5. Filter out already-existing terms
    const newTerms = Array.from(allDiscoveredWords?.values())?.filter(
      t => !existingSet?.has(t?.word)
    );

    const skipped = allDiscoveredWords?.size - newTerms?.length;

    if (newTerms?.length === 0) {
      return { added: 0, skipped, terms: [], audioSucceeded: 0, audioFailed: 0 };
    }

    onProgress?.(`Adding ${newTerms?.length} new glossary terms with AI-generated definitions...`);

    // 6. Insert new terms with AI-generated definitions.
    //    ai_generated=true and review_status='pending_review' flags each entry
    //    for admin review before it is considered approved/published.
    //    audio_generation_status='pending' and audio_quality_flag='pending_review'
    //    will be updated after TTS generation below.
    const insertPayload = newTerms?.map(t => ({
      term: t?.word,
      pronunciation: t?.pronunciation || null,
      definition: t?.definition,
      category: 'archaic',
      difficulty: t?.difficulty,
      play_id: playId || null,
      created_by: userId || null,
      ai_generated: true,
      review_status: 'pending_review',
      audio_generation_status: 'pending',
      audio_quality_flag: 'pending_review'
    }));

    // Insert with upsert to avoid race conditions
    const { data: inserted, error: insertError } = await supabase
      ?.from('glossary_terms')
      ?.insert(insertPayload)
      ?.select();

    if (insertError) {
      console.error('Error inserting glossary terms:', insertError);
      throw insertError;
    }

    const addedTerms = inserted || newTerms;

    // 7. Auto-generate pronunciation audio for newly inserted terms
    //    This runs after definitions are saved, non-blocking per-term (errors caught internally)
    let audioSucceeded = 0;
    let audioFailed = 0;

    if (inserted?.length > 0) {
      onProgress?.(`Generating pronunciation audio for ${inserted?.length} terms...`);

      const audioTerms = inserted?.map(t => ({ id: t?.id, term: t?.term }));

      try {
        const audioResult = await generateAudioForTerms(
          audioTerms,
          (completed, total, currentTerm) => {
            if (currentTerm) {
              onProgress?.(`Generating audio: "${currentTerm}" (${completed + 1}/${total})...`);
            }
          }
        );
        audioSucceeded = audioResult?.succeeded || 0;
        audioFailed = audioResult?.failed || 0;
      } catch (audioError) {
        console.error('Audio generation batch error:', audioError);
        audioFailed = inserted?.length;
      }
    }

    onProgress?.('Glossary auto-population and audio generation complete.');

    return {
      added: addedTerms?.length || newTerms?.length,
      skipped,
      terms: addedTerms,
      audioSucceeded,
      audioFailed
    };
  } catch (error) {
    console.error('Error in scanAndPopulateGlossary:', error);
    throw error;
  }
};
